<script lang="ts">
	import { Toggle, toast } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import PinUnlock from 'src/components/PinUnlock.svelte';
	import {
		deviceControlOnOff,
		getDeviceOnOffState,
		type DeviceOnOffState,
	} from 'src/helpers/device_control_on_off';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { devices } from 'src/stores/lists/devices';
	import type { Device } from 'src/types/Device';

	export let device: Device;
	export let status: any;
	export let settings: any;

	let loadingRequest = false;
	let showPinModal = false;

	$: deviceStateStore = getDeviceState(device?.id);
	$: deviceState = $deviceStateStore;
	$: isLoading = deviceState?.isLoading || loadingRequest;
	$: onOffState =
		device && status
			? (getDeviceOnOffState(
					device,
					status,
					settings,
					deviceState?.type,
				) as DeviceOnOffState | null)
			: null;
	$: pinLocked =
		typeof $pin_unlocked_devices[device?.id] === 'undefined' && $devices[device?.id]?.pin_lock;
	$: canControl = !!onOffState && !onOffState.readOnly && !onOffState.disabled;
	$: isActive = !!onOffState?.isOn;

	async function executeToggle() {
		if (!onOffState || !canControl) return;
		loadingRequest = true;
		try {
			const result = await deviceControlOnOff(onOffState.controlParams);
			if (!result.success) {
				toast.open({ type: 'danger', title: $format('error_global') });
			}
		} catch {
			toast.open({ type: 'danger', title: $format('error_global') });
		} finally {
			loadingRequest = false;
		}
	}

	function handleClick(event: MouseEvent) {
		event.stopPropagation();
		event.preventDefault();
		if (!canControl || isLoading) return;
		if (pinLocked) {
			showPinModal = true;
			return;
		}
		executeToggle();
	}

	function handlePinUnlock() {
		unlockPin(device, pinLocked);
		executeToggle();
	}
</script>

{#if canControl}
	<Toggle
		id="device-table-state-{device.id}"
		class="d-inline-flex"
		size="m"
		checked={isActive}
		disabled={isLoading}
		aria-label={$format('label_turn_device', { default: 'Turn device' })}
		on:click={handleClick}
	/>
{:else}
	<span class="text-color-muted">—</span>
{/if}

{#if showPinModal}
	<PinUnlock
		bind:showModal={showPinModal}
		on:unlock={handlePinUnlock}
		on:cancel={() => (showPinModal = false)}
	/>
{/if}
